import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ReserveHeader } from "@/components/ReserveHeader";
import { refundTipAllocation, retryTipPayout } from "@/lib/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/tip-audit")({
  head: () => ({ meta: [{ title: "Tip Allocation Audit — Admin" }] }),
  component: TipAuditPage,
});

type AuditRow = {
  id: string;
  shift_id: string;
  venue_id: string | null;
  actor_user_id: string | null;
  actor_role: string | null;
  event: string;
  idempotency_key: string | null;
  tip_amount_pence: number | null;
  status: string | null;
  stripe_event_id: string | null;
  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;
  error_message: string | null;
  created_at: string;
};

type AllocRow = {
  id: string;
  shift_id: string;
  venue_id: string;
  idempotency_key: string;
  action: string;
  tip_amount_pence: number;
  status: string;
  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;
  error_message: string | null;
  created_at: string;
};

function TipAuditPage() {
  const [allocations, setAllocations] = useState<AllocRow[]>([]);
  const [events, setEvents] = useState<AuditRow[]>([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = async () => {
    const [{ data: a }, { data: e }] = await Promise.all([
      supabase.from("tip_allocations").select("*").order("created_at", { ascending: false }).limit(100),
      supabase.from("tip_allocation_audit_log").select("*").order("created_at", { ascending: false }).limit(300),
    ]);
    setAllocations((a ?? []) as AllocRow[]);
    setEvents((e ?? []) as AuditRow[]);
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const handleRefund = async (r: AllocRow) => {
    const reason = window.prompt(`Refund tip £${(r.tip_amount_pence / 100).toFixed(2)}?\nEnter a reason (visible in audit + bartender notice):`);
    if (!reason || reason.trim().length < 3) return;
    setBusyId(r.id);
    try {
      const env = getStripeEnvironment();
      const res = await refundTipAllocation({ data: { allocation_id: r.id, environment: env, reason: reason.trim() } });
      if ("error" in res) { toast.error(res.error); return; }
      toast.success(`Refunded £${(res.refunded_pence / 100).toFixed(2)}${res.reversed_payouts ? ` · ${res.reversed_payouts} payout(s) reversed` : ""}`);
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Refund failed");
    } finally { setBusyId(null); }
  };

  const handleRetry = async (r: AllocRow) => {
    if (!window.confirm(`Retry tip payout for shift ${r.shift_id.slice(0, 8)}?`)) return;
    setBusyId(r.id);
    try {
      const env = getStripeEnvironment();
      const res = await retryTipPayout({ data: { allocation_id: r.id, environment: env } });
      if ("error" in res) { toast.error(res.error); return; }
      toast.success(`Sweep complete — paid ${res.paid}, failed ${res.failed}, skipped ${res.skipped}`);
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Retry failed");
    } finally { setBusyId(null); }
  };

  const f = filter.trim().toLowerCase();
  const filteredAllocs = f
    ? allocations.filter((r) => r.shift_id.includes(f) || r.idempotency_key.toLowerCase().includes(f) || r.status.toLowerCase().includes(f))
    : allocations;
  const filteredEvents = f
    ? events.filter((r) =>
        r.shift_id.includes(f) ||
        (r.idempotency_key ?? "").toLowerCase().includes(f) ||
        r.event.toLowerCase().includes(f) ||
        (r.error_message ?? "").toLowerCase().includes(f))
    : events;

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <ReserveHeader role="bartender" />
      <main className="mx-auto max-w-6xl px-6 py-12 lg:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Compliance</p>
            <h1 className="mt-3 font-display text-3xl lg:text-4xl">Tip Allocation Audit</h1>
            <span className="gold-divider mt-4" />
            <p className="mt-4 text-sm text-muted-foreground max-w-xl">
              Every tip allocation attempt and outcome — including idempotency replays and Stripe failures.
            </p>
          </div>
          <Link to="/admin" className="border border-border px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground hover:border-foreground hover:text-foreground">
            ← Console
          </Link>
        </div>

        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by shift ID, idempotency key, event, status, or error keyword…"
          className="mt-8 w-full border border-border bg-background px-3 py-2 text-sm"
        />

        {loading && <p className="mt-8 text-sm text-muted-foreground">Loading…</p>}

        <section className="mt-10">
          <h2 className="font-display text-xl">Tip Allocations</h2>
          <div className="mt-4 overflow-x-auto border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-2">Created</th>
                  <th className="text-left p-2">Shift</th>
                  <th className="text-left p-2">Action</th>
                  <th className="text-left p-2">Amount</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Idempotency Key</th>
                  <th className="text-left p-2">Error</th>
                  <th className="text-left p-2">Admin</th>
                </tr>
              </thead>
              <tbody>
                {filteredAllocs.map((r) => {
                  const canRefund = r.action === "pay" && ["paid", "payout_succeeded", "payout_failed", "disputed"].includes(r.status);
                  const canRetry = r.action === "pay" && r.status === "payout_failed";
                  return (
                    <tr key={r.id} className="border-t border-border">
                      <td className="p-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-GB")}</td>
                      <td className="p-2 font-mono">{r.shift_id.slice(0, 8)}…</td>
                      <td className="p-2">{r.action}</td>
                      <td className="p-2">£{(r.tip_amount_pence / 100).toFixed(2)}</td>
                      <td className="p-2">{r.status}</td>
                      <td className="p-2 font-mono">{r.idempotency_key.slice(0, 12)}…</td>
                      <td className="p-2 text-destructive">{r.error_message ?? ""}</td>
                      <td className="p-2 whitespace-nowrap">
                        {canRetry && (
                          <button
                            disabled={busyId === r.id}
                            onClick={() => handleRetry(r)}
                            className="mr-2 border border-border px-2 py-1 text-[10px] tracking-wide-luxury hover:border-foreground disabled:opacity-50"
                          >Retry</button>
                        )}
                        {canRefund && (
                          <button
                            disabled={busyId === r.id}
                            onClick={() => handleRefund(r)}
                            className="border border-destructive px-2 py-1 text-[10px] tracking-wide-luxury text-destructive hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50"
                          >Refund</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!loading && filteredAllocs.length === 0 && (
                  <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">No allocations match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-xl">Event Log</h2>
          <div className="mt-4 overflow-x-auto border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-2">When</th>
                  <th className="text-left p-2">Event</th>
                  <th className="text-left p-2">Actor</th>
                  <th className="text-left p-2">Shift</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Idem</th>
                  <th className="text-left p-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="p-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-GB")}</td>
                    <td className="p-2">{r.event}</td>
                    <td className="p-2">{r.actor_role ?? "—"}</td>
                    <td className="p-2 font-mono">{r.shift_id.slice(0, 8)}…</td>
                    <td className="p-2">{r.status ?? ""}</td>
                    <td className="p-2 font-mono">{(r.idempotency_key ?? "").slice(0, 12)}{r.idempotency_key ? "…" : ""}</td>
                    <td className="p-2 text-destructive">{r.error_message ?? ""}</td>
                  </tr>
                ))}
                {!loading && filteredEvents.length === 0 && (
                  <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No events match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
